import { Injectable } from '@angular/core';

export interface GoogleCredentialResponse {
  credential: string;
  select_by: string;
}

interface GoogleIdentityApi {
  initialize(configuration: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
    auto_select: boolean;
    cancel_on_tap_outside: boolean;
    use_fedcm_for_prompt: boolean;
  }): void;
  prompt(): void;
  cancel(): void;
  disableAutoSelect(): void;
}

interface GoogleIdentityWindow {
  accounts: {
    id: GoogleIdentityApi;
  };
}

declare global {
  interface Window {
    google?: GoogleIdentityWindow;
  }
}

@Injectable({ providedIn: 'root' })
export class GoogleIdentityService {
  private readonly scriptId = 'google-identity-services';
  private loadingPromise: Promise<GoogleIdentityApi> | null = null;

  initializeOneTap(clientId: string, callback: (response: GoogleCredentialResponse) => void): Promise<void> {
    return this.load().then((google) => {
      google.initialize({
        client_id: clientId,
        callback,
        auto_select: false,
        cancel_on_tap_outside: true,
        use_fedcm_for_prompt: true,
      });
      google.prompt();
    });
  }

  cancelOneTap(): void {
    window.google?.accounts.id.cancel();
  }

  disableAutoSelect(): void {
    window.google?.accounts.id.disableAutoSelect();
  }

  private load(): Promise<GoogleIdentityApi> {
    if (window.google?.accounts.id) {
      return Promise.resolve(window.google.accounts.id);
    }
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = new Promise<GoogleIdentityApi>((resolve, reject) => {
      const existing = document.getElementById(this.scriptId) as HTMLScriptElement | null;
      const script = existing ?? document.createElement('script');
      const onLoad = () => {
        if (window.google?.accounts.id) {
          resolve(window.google.accounts.id);
          return;
        }
        reject(new Error('Google Identity Services did not initialize'));
      };
      const onError = () => reject(new Error('Google Identity Services could not be loaded'));

      script.addEventListener('load', onLoad, { once: true });
      script.addEventListener('error', onError, { once: true });
      if (!existing) {
        script.id = this.scriptId;
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        document.head.appendChild(script);
      }
    }).catch((error) => {
      this.loadingPromise = null;
      throw error;
    });

    return this.loadingPromise;
  }
}
